"use client";

import { c as _c } from "react/compiler-runtime";
import type { ToastRecord, ToastVariant } from "@/types/toast";
import type { Easing, PanInfo } from "motion/react";
import type { JSX, KeyboardEvent } from "react";
import { useAutoDismiss } from "@/hooks/use-auto-dismiss";
import { cn } from "@/lib/utils";
import { animate, motion, useMotionValue } from "motion/react";
import type { BaseSVGProps } from "@slipstream/ui";
import { CircleAlert, CircleCheck, Info, X } from "@slipstream/ui";
const EXIT_EASE = [0.215, 0.61, 0.355, 1] as const satisfies Easing;
const SWIPE_DISMISS_OFFSET = 72;
const SWIPE_DISMISS_VELOCITY = 400;
type IconType = ({
  role,
  strokeWidth,
  ...svg
}: BaseSVGProps) => JSX.Element;
const variantIcon = {
  default: null,
  success: CircleCheck,
  error: CircleAlert,
  info: Info
} satisfies Record<ToastVariant, IconType | null>;
const variantIconClass = {
  default: "",
  success: "text-success",
  error: "text-destructive",
  info: "text-muted-foreground"
} satisfies Record<ToastVariant, string>;
interface ToastItemProps {
  toast: ToastRecord;
  paused: boolean;
  onDismiss: (id: string) => void;
}
export function ToastItem(t0) {
  const $ = _c(47);
  const {
    toast,
    paused,
    onDismiss
  } = t0;
  const x = useMotionValue(0);
  let t1;
  if ($[0] !== onDismiss || $[1] !== toast.id) {
    t1 = () => onDismiss(toast.id);
    $[0] = onDismiss;
    $[1] = toast.id;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const dismiss = t1;
  let t2;
  if ($[3] !== dismiss || $[4] !== paused || $[5] !== toast.duration || $[6] !== toast.revision) {
    t2 = {
      duration: toast.duration,
      paused,
      resetKey: toast.revision,
      onExpire: dismiss
    };
    $[3] = dismiss;
    $[4] = paused;
    $[5] = toast.duration;
    $[6] = toast.revision;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  useAutoDismiss(t2);
  let t3;
  if ($[8] !== dismiss || $[9] !== x) {
    t3 = function handleDragEnd(_, info) {
      const swipedOut = info.offset.x > SWIPE_DISMISS_OFFSET || info.velocity.x > SWIPE_DISMISS_VELOCITY;
      if (swipedOut) {
        animate(x, 400, {
          duration: 0.2,
          ease: "easeIn"
        });
        dismiss();
      } else {
        animate(x, 0, {
          type: "spring",
          stiffness: 400,
          damping: 30
        });
      }
    };
    $[8] = dismiss;
    $[9] = x;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const handleDragEnd = t3;
  let t4;
  if ($[11] !== dismiss) {
    t4 = function handleKeyDown(event) {
      if (event.key === "Escape") {
        event.stopPropagation();
        dismiss();
      }
    };
    $[11] = dismiss;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const handleKeyDown = t4;
  const Icon = variantIcon[toast.variant];
  const isError = toast.variant === "error";
  const t5 = isError ? "alert" : "status";
  const t6 = isError ? "assertive" : "polite";
  let t7;
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      opacity: 0,
      y: 16,
      scale: 0.96
    };
    t8 = {
      opacity: 1,
      y: 0,
      scale: 1
    };
    $[13] = t7;
    $[14] = t8;
  } else {
    t7 = $[13];
    t8 = $[14];
  }
  let t10;
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      opacity: 0,
      scale: 0.95,
      transition: {
        duration: 0.18,
        ease: EXIT_EASE
      }
    };
    t10 = {
      type: "spring",
      stiffness: 320,
      damping: 28
    };
    $[15] = t10;
    $[16] = t9;
  } else {
    t10 = $[15];
    t9 = $[16];
  }
  let t11;
  let t12;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      left: 0,
      right: 0
    };
    t12 = {
      left: 0,
      right: 1
    };
    $[17] = t11;
    $[18] = t12;
  } else {
    t11 = $[17];
    t12 = $[18];
  }
  let t13;
  if ($[19] !== x) {
    t13 = {
      x
    };
    $[19] = x;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== Icon || $[22] !== toast.variant) {
    t14 = Icon && <Icon aria-hidden={true} className={cn("mt-0.5 size-5 shrink-0", variantIconClass[toast.variant])} />;
    $[21] = Icon;
    $[22] = toast.variant;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== toast.title) {
    t15 = <p className="text-sm leading-5 font-medium">{toast.title}</p>;
    $[24] = toast.title;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== toast.description) {
    t16 = toast.description && <p className="text-muted-foreground text-sm leading-5">{toast.description}</p>;
    $[26] = toast.description;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== t15 || $[29] !== t16) {
    t17 = <div className="flex min-w-0 flex-1 flex-col gap-1">{t15}{t16}</div>;
    $[28] = t15;
    $[29] = t16;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== dismiss || $[32] !== toast.action) {
    t18 = toast.action && <button type="button" onClick={() => {
      toast.action?.onClick();
      dismiss();
    }} className="border-border hover:bg-muted focus-visible:ring-ring shrink-0 rounded-md border px-2 py-1 text-xs font-medium focus-visible:ring-2 focus-visible:outline-none">{toast.action.label}</button>;
    $[31] = dismiss;
    $[32] = toast.action;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <X aria-hidden={true} className="size-4" />;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== dismiss) {
    t20 = <button type="button" onClick={dismiss} aria-label="Dismiss notification" className="text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-ring -m-1 shrink-0 rounded-md p-1 focus-visible:ring-2 focus-visible:outline-none">{t19}</button>;
    $[35] = dismiss;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== handleDragEnd || $[38] !== handleKeyDown || $[39] !== t13 || $[40] !== t14 || $[41] !== t17 || $[42] !== t18 || $[43] !== t20 || $[44] !== t5 || $[45] !== t6) {
    t21 = <motion.li role={t5} aria-live={t6} aria-atomic={true} tabIndex={0} layout={true} initial={t7} animate={t8} exit={t9} transition={t10} drag="x" dragConstraints={t11} dragElastic={t12} dragMomentum={false} onDragEnd={handleDragEnd} onKeyDown={handleKeyDown} style={t13} className="border-border bg-popover text-popover-foreground focus-visible:ring-ring pointer-events-auto flex w-full items-start gap-3 rounded-lg border p-4 shadow-lg outline-none focus-visible:ring-2">{t14}{t17}{t18}{t20}</motion.li>;
    $[37] = handleDragEnd;
    $[38] = handleKeyDown;
    $[39] = t13;
    $[40] = t14;
    $[41] = t17;
    $[42] = t18;
    $[43] = t20;
    $[44] = t5;
    $[45] = t6;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  return t21;
}